import { Timestamp } from 'firebase/firestore';
import { toMillis } from './dates';
import { round2 } from './money';
import {
  MergedPaymentData,
  MergedPaymentSource,
  PaymentData,
  PaymentDeduction,
  PaymentStatus
} from '../types/payment';

// A merged payment is only as far along as its least advanced part, so the
// combined status never claims money was paid when part of it was not.
const STATUS_RANK: Record<PaymentStatus, number> = {
  PENDING: 0,
  PROCESSED: 1,
  PAID: 2
};

export { round2 };

export { toMillis };

// The pieces of a payment that merging cares about. Both a live payment and the
// snapshot of an already merged one satisfy it.
interface PaymentLike {
  createdAt?: any;
  invoiceNumbers?: string[];
  monthlyDeductions?: PaymentDeduction[];
}

export const sortByCreatedAtAsc = <T extends { createdAt?: any }>(payments: T[]): T[] =>
  [...payments].sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));

export interface MergeValidation {
  valid: boolean;
  error?: string;
  /** Non-blocking things the admin should see before confirming */
  warnings: string[];
}

export const validateMergeSelection = (payments: PaymentData[]): MergeValidation => {
  const warnings: string[] = [];

  if (payments.length < 2) {
    return { valid: false, error: 'Select at least two payments to merge.', warnings };
  }

  const driverIds = Array.from(new Set(payments.map(p => p.driverId)));
  if (driverIds.length > 1) {
    return {
      valid: false,
      error: 'Payments can only be merged when they belong to the same driver.',
      warnings
    };
  }

  const statuses = Array.from(new Set(payments.map(p => p.status)));
  if (statuses.length > 1) {
    warnings.push(
      `The selected payments have different statuses (${statuses.join(', ')}). ` +
      `The merged payment will use ${lowestStatus(payments)}.`
    );
  }

  const duplicateInvoices = findDuplicateInvoiceNumbers(payments);
  if (duplicateInvoices.length > 0) {
    warnings.push(
      `${duplicateInvoices.join(', ')} appear${duplicateInvoices.length === 1 ? 's' : ''} ` +
      'in more than one of the selected payments. The invoice is listed once, but its ' +
      'amount is still counted for each payment it came from.'
    );
  }

  const cuts = Array.from(new Set(payments.map(p => p.percentageCut)));
  if (cuts.length > 1) {
    warnings.push(
      `The selected payments use different percentage cuts (${cuts.map(c => `${c}%`).join(', ')}). ` +
      'The merged payment shows the blended rate.'
    );
  }

  return { valid: true, warnings };
};

export const lowestStatus = (payments: PaymentData[]): PaymentStatus =>
  payments.reduce<PaymentStatus>((lowest, payment) => {
    const status = payment.status || 'PENDING';
    return STATUS_RANK[status] < STATUS_RANK[lowest] ? status : lowest;
  }, 'PAID');

export const findDuplicateInvoiceNumbers = (payments: PaymentLike[]): string[] => {
  const counts = new Map<string, number>();
  payments.forEach(payment => {
    (payment.invoiceNumbers || []).forEach(invoiceNumber => {
      counts.set(invoiceNumber, (counts.get(invoiceNumber) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([invoiceNumber]) => invoiceNumber);
};

// Every invoice from every selected payment, oldest payment first, listed once.
export const mergeInvoiceNumbers = (payments: PaymentLike[]): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];
  sortByCreatedAtAsc(payments).forEach(payment => {
    (payment.invoiceNumbers || []).forEach(invoiceNumber => {
      if (!seen.has(invoiceNumber)) {
        seen.add(invoiceNumber);
        merged.push(invoiceNumber);
      }
    });
  });
  return merged;
};

// The same deduction (e.g. insurance) can be charged on more than one payment.
// Roll those into a single line with the combined amount so the totals still add
// up, rather than dropping one of them.
export const mergeMonthlyDeductions = (payments: PaymentLike[]): PaymentDeduction[] => {
  const combined = new Map<string, PaymentDeduction>();
  sortByCreatedAtAsc(payments).forEach(payment => {
    (payment.monthlyDeductions || []).forEach(deduction => {
      const key = `${deduction.name}||${deduction.description || ''}`;
      const existing = combined.get(key);
      if (existing) {
        existing.amount = round2(existing.amount + deduction.amount);
      } else {
        combined.set(key, { ...deduction, amount: round2(deduction.amount) });
      }
    });
  });
  return Array.from(combined.values());
};

export const toMergedSource = (payment: PaymentData): MergedPaymentSource => {
  const source: MergedPaymentSource = {
    id: payment.id,
    createdAt: payment.createdAt,
    invoiceNumbers: payment.invoiceNumbers || [],
    totalBeforeDeductions: payment.totalBeforeDeductions || 0,
    totalAfterDeductions: payment.totalAfterDeductions || 0,
    percentageCut: payment.percentageCut || 0,
    percentageCutAmount: payment.percentageCutAmount || 0,
    monthlyDeductions: payment.monthlyDeductions || [],
    status: payment.status || 'PENDING'
  };
  if (payment.notes) source.notes = payment.notes;
  return source;
};

// A merged payment that is merged again should keep the full history rather than
// nesting merges inside merges.
export const flattenSources = (payments: PaymentData[]): MergedPaymentSource[] =>
  sortByCreatedAtAsc(payments).flatMap(payment =>
    payment.mergedFrom && payment.mergedFrom.length > 0
      ? payment.mergedFrom
      : [toMergedSource(payment)]
  );

/**
 * Builds the payment document that replaces the selected payments. Totals are
 * summed from the stored payment figures rather than recalculated so the merged
 * record always owes the driver exactly what the originals did.
 */
export const buildMergedPayment = (
  payments: PaymentData[],
  now: Timestamp = Timestamp.now()
): MergedPaymentData => {
  const validation = validateMergeSelection(payments);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const ordered = sortByCreatedAtAsc(payments);
  // Ordering by the underlying payments (rather than by the selected records)
  // keeps a merge of a merge in true chronological order.
  const sources = sortByCreatedAtAsc(flattenSources(payments));
  const totalBeforeDeductions = round2(
    ordered.reduce((sum, p) => sum + (p.totalBeforeDeductions || 0), 0)
  );
  const totalAfterDeductions = round2(
    ordered.reduce((sum, p) => sum + (p.totalAfterDeductions || 0), 0)
  );
  const percentageCutAmount = round2(
    ordered.reduce((sum, p) => sum + (p.percentageCutAmount || 0), 0)
  );

  const cuts = Array.from(new Set(ordered.map(p => p.percentageCut || 0)));
  const percentageCut = cuts.length === 1
    ? cuts[0]
    : totalBeforeDeductions > 0
      ? round2((percentageCutAmount / totalBeforeDeductions) * 100)
      : 0;

  const notes = ordered
    .map(p => p.notes)
    .filter((note): note is string => typeof note === 'string' && note.trim().length > 0)
    .join('\n');

  const merged: MergedPaymentData = {
    createdAt: now,
    mergedAt: now,
    driverName: ordered[0].driverName,
    driverId: ordered[0].driverId,
    invoiceNumbers: mergeInvoiceNumbers(sources),
    totalBeforeDeductions,
    totalAfterDeductions,
    percentageCut,
    percentageCutAmount,
    monthlyDeductions: mergeMonthlyDeductions(sources),
    status: lowestStatus(ordered),
    mergedFrom: sources,
    mergedSourceIds: ordered.map(payment => payment.id)
  };

  if (notes) merged.notes = notes;

  return merged;
};
